import { useCallback, useReducer, useMemo } from 'react';
import {
  hasValue,
  isEmpty,
  isNumeric,
  isString,
  useTemp,
  useFormInputHandler,
} from 'swipeclock-frontend';

// Prompt types for categorization
const PROMPT_TYPES = {
  ALPHANUMERIC: 'alphanumeric',
  DECIMAL: 'decimal',
  INTEGER: 'integer',
  NUMERIC: 'numeric', // currency
  DROPDOWN: 'dropdown',
};

/**
 * Hook to access and process clock prompts from cache
 */
export function usePrompts() {
  const { temp: cache } = useTemp('webClock');
  const { clockPrompts = [] } = cache || {};

  // Process and categorize prompts
  const processedPrompts = useMemo(() => {
    if (!clockPrompts || !clockPrompts.length) return [];

    return clockPrompts.map((prompt) => {
      const {
        variableName,
        name,
        displayText,
        isRequired,
        min,
        max,
        allowableValues,
        negativeAllowed,
        allowNullEntry,
        assignment,
        numericOnly,
        integerOnly,
        decimalOnly,
      } = prompt;

      // Determine prompt type using an array of condition rules
      const promptTypeRules = [
        // Check for dropdowns first (highest priority)
        {
          check: () => allowableValues && allowableValues.length > 0,
          type: PROMPT_TYPES.DROPDOWN,
        },
        // Labor assignment cases
        {
          check: () => assignment === 'Labor' && numericOnly,
          type: PROMPT_TYPES.INTEGER,
        },
        {
          check: () => assignment === 'Labor' && !numericOnly,
          type: PROMPT_TYPES.ALPHANUMERIC,
        },
        // Numeric assignment cases
        {
          check: () => assignment === 'Numeric' && integerOnly,
          type: PROMPT_TYPES.INTEGER,
        },
        {
          check: () => assignment === 'Numeric' && decimalOnly,
          type: PROMPT_TYPES.NUMERIC,
        },
        // General cases (not dependent on specific assignment)
        {
          check: () => integerOnly,
          type: PROMPT_TYPES.INTEGER,
        },
        {
          check: () => decimalOnly,
          type: PROMPT_TYPES.NUMERIC,
        },
        // Other non-Labor, non-Numeric assignments
        {
          check: () =>
            assignment !== 'Labor' && assignment !== 'Numeric' && numericOnly,
          type: PROMPT_TYPES.INTEGER,
        },
        {
          check: () =>
            assignment !== 'Labor' &&
            assignment !== 'Numeric' &&
            !integerOnly &&
            !decimalOnly &&
            !numericOnly,
          type: PROMPT_TYPES.ALPHANUMERIC,
        },
        // Default fallback (though with our conditions, this should never be reached)
        {
          check: () => true,
          type: PROMPT_TYPES.ALPHANUMERIC,
        },
      ];

      // Find the first matching rule
      const matchingRule = promptTypeRules.find((rule) => rule.check());
      const promptType = matchingRule.type;

      // Generate options array for dropdowns
      const options = allowableValues
        ? allowableValues.map((value) => ({
            value,
            label: value,
          }))
        : [];

      // Format the prompt for easier consumption in the Form
      return {
        id: variableName || name,
        name: variableName || name,
        label: displayText || name,
        type: promptType,
        required: isRequired === 'true',
        minLength: Number(min) || 0, // Use min for length
        maxLength: Number(max) || 99999999, // Use max for length
        options,
        negativeAllowed: negativeAllowed || false,
        allowNullEntry: allowNullEntry || false,
        originalPrompt: prompt, // Keep original for reference
      };
    });
  }, [clockPrompts]);

  return { prompts: processedPrompts };
}

/**
 * Hook to provide form handling for clock prompts
 */
export function usePromptsForm() {
  const { prompts } = usePrompts();
  const { errorContext, validationCallback } = useFormValidation(prompts);
  const ids = [...prompts.map((p) => p.id)];
  const formBag = useFormInputHandler({
    errorContext,
    ids,
    validationCallback,
  });

  return {
    errorContext,
    formBag,
    prompts,
  };
}

/**
 * Internal hook for form validation and handling
 */
function useFormValidation(prompts = []) {
  const initialState = { errors: {} };

  const reducer = (state, action) => {
    switch (action.type) {
      case 'SET_ERROR':
        return {
          ...state,
          errors: {
            ...state.errors,
            [action.id]: action.error,
          },
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const setError = useCallback((id, error) => {
    dispatch({
      type: 'SET_ERROR',
      id,
      error,
    });
  }, []);

  // Validate a specific prompt
  const validatePrompt = useCallback((prompt, value) => {
    const {
      type,
      label,
      required,
      minLength: min,
      maxLength: max,
      negativeAllowed,
    } = prompt;

    // Skip validation if value is empty and not required
    if (!required && isEmpty(value)) {
      return null;
    }

    // Required field validation
    if (required && !hasValue(value)) {
      return `${label} is required.`;
    }

    // Type-specific validation
    switch (type) {
      case PROMPT_TYPES.ALPHANUMERIC: {
        // String validation
        if (!isString(value) || value.length < min || value.length > max) {
          return `${label} must be a valid text between ${min} and ${max} characters.`;
        }
        break;
      }
      case PROMPT_TYPES.DECIMAL: {
        // Currency validation (decimal is money)
        if (!isNumeric(value.replace(/[$,]/g, ''))) {
          return `${label} must be a valid currency amount.`;
        }

        const currencyValue = parseFloat(value.replace(/[$,]/g, ''));

        if (!negativeAllowed && currencyValue < 0) {
          return `${label} cannot be negative.`;
        }

        if (value.length < min || value.length > max) {
          return `${label} must be between ${min} and ${max} characters.`;
        }

        break;
      }
      case PROMPT_TYPES.INTEGER: {
        // Integer validation
        if (!isNumeric(value) || !Number.isInteger(+value)) {
          return `${label} must be an integer.`;
        }

        const intValue = parseInt(value, 10);

        if (!negativeAllowed && intValue < 0) {
          return `${label} cannot be negative.`;
        }

        if (value.length < min || value.length > max) {
          return `${label} must be between ${min} and ${max} characters.`;
        }

        break;
      }
      case PROMPT_TYPES.NUMERIC: {
        // Float validation (numeric is float)
        if (!isNumeric(value)) {
          return `${label} must be a numeric value.`;
        }

        const numValue = parseFloat(value);

        if (!negativeAllowed && numValue < 0) {
          return `${label} cannot be negative.`;
        }

        if (value.length < min || value.length > max) {
          return `${label} must be between ${min} and ${max} characters.`;
        }

        // Check decimal places (max 2)
        const decimalPlaces = String(value).includes('.')
          ? String(value).split('.')[1].length
          : 0;

        if (decimalPlaces > 2) {
          return `${label} must have at most 2 decimal places.`;
        }
        break;
      }
      case PROMPT_TYPES.DROPDOWN: {
        // Dropdown validation
        if (required && !hasValue(value)) {
          return `Please select a ${label}.`;
        }
        break;
      }

      default:
        break;
    }

    return null;
  }, []);

  // Validation callback for form elements
  const validationCallback = useCallback(
    (id, value, _ref) => {
      const prompt = prompts.find((p) => p.id === id);
      if (!prompt) return null;

      return validatePrompt(prompt, value, _ref.current);
    },
    [prompts, validatePrompt]
  );

  return {
    errorContext: {
      errors: state.errors,
      setError,
    },
    validationCallback,
    values: state.values,
  };
}

export function usePrepareClockPromptsForPunchRequest({
  isValid,
  validationRef,
}) {
  const { prompts } = usePrompts();

  const prepareClockPrompts = useCallback(() => {
    try {
      const valid = isValid();

      if (!valid) {
        throw new Error('Form is invalid');
      }

      const clockPrompts = Object.keys(validationRef.current).map((key) => {
        const currentValidationRef = validationRef.current[key];
        let answerValue = hasValue(currentValidationRef.value)
          ? currentValidationRef.value
          : '';

        const prompt = prompts.find((p) => p.id === key);

        if (prompt?.type === PROMPT_TYPES.DECIMAL && isString(answerValue)) {
          answerValue = answerValue.replace(/\$/g, '');
        }

        return {
          name: key,
          answer: answerValue,
        };
      });

      return clockPrompts ?? [];
    } catch (error) {
      console.error('Error during form submission:', error);
      return null;
    }
  }, []);

  return {
    prepareClockPrompts,
  };
}
